<template>
  <div class="w-full max-w-[480px] mx-auto mt-[23px]">
    <BackButton
      v-if="!webviewStore.isActive"
      class="fixed left-3 top-[3px] desktop:top-2 z-50"
      @click="handleBackClick"
    />

    <div class="text-[24px] leading-[28px] font-medium text-gray-800 mb-[30px]">
      Confirm your plan change
    </div>

    <div
      v-if="oldPlan"
      class="bg-white border border-[#ebedef] rounded-[10px] p-[15px]"
    >
      <div class="flex items-center justify-between">
        <div class="text-[16px] leading-[20px] font-medium text-gray-400">
          {{ oldPlan.name }}
        </div>
        <Pill>Current</Pill>
      </div>
      <div class="mt-2.5 space-y-1 text-[14px] leading-[20px] text-gray-400">
        <div v-if="user?.totalFilesLimit">
          {{ user.totalFilesLimit }} copyrights
        </div>
        <div
          v-for="(price, priceIndex) in oldPlanPrices"
          :key="`price-${priceIndex}`"
        >
          {{ formatPriceLocal(price) }} {{ t(price.details.suffix) }}
        </div>
      </div>
    </div>
    <div class="-mt-px flex justify-center">
      <ArrowUpIcon class="size-5 rotate-180 text-gray-200" />
    </div>

    <div
      v-if="newPlan"
      class="mt-[15px] bg-white border border-[#ebedef] rounded-[10px] p-[15px]"
    >
      <div class="flex items-center justify-between">
        <div class="text-[16px] leading-[20px] font-medium text-gray-800">
          {{ newPlan.name }}
        </div>
        <Pill variant="success">New</Pill>
      </div>
      <div class="mt-2.5 space-y-1 text-[14px] leading-[20px] text-gray-600">
        <div v-if="newPlanItem?.maxUnits">
          {{ newPlanItem.maxUnits }} copyrights
        </div>
        <div v-if="newPlanItem" class="flex items-center gap-1.5">
          <template
            v-for="(price, priceIndex) in newPlanItem.prices"
            :key="`price-${priceIndex}`"
          >
            <span v-if="priceIndex">•</span>
            <span v-if="!priceIndex">
              {{ formatPriceLocal(price) }}
              /{{ price.details?.billingCycleType }}
            </span>
            <span v-else>{{ formatPriceLocal(price) }} per copyright</span>
          </template>
        </div>
      </div>
    </div>

    <Alert variant="warning" class="mt-4">
      You will be billed immediately and your new plan will take effect upon
      completing your purchase.
    </Alert>

    <RouterLink to="/change-plans/checkout" class="block mt-4">
      <Button variant="primary" size="lg" class="w-full">
        Confirm and purchase
      </Button>
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import type {
  BillingPrice,
  PlanPriceItem,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { t } from '@/shared/i18n'
import { useUserStore } from '@/shared/stores/user'
import { useWebviewStore } from '@/shared/stores/webview'
import { useChangePlanEstimate } from '@/features/billing/composables/useChangePlanEstimate'
import { formatPrice } from '@/shared/utils/money'
import { Alert, Button, Pill } from '@dacr/library'
import { ArrowUpIcon } from '@dacr/icons'
import BackButton from '@/shared/components/BackButton.vue'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()
const router = useRouter()

const handleBackClick = () => {
  router.push('/change-plans')
}

useChangePlanEstimate()

const user = computed(() => userStore.user)

const oldPlan = computed(() => billingStore.billing?.plan)
const oldPlanPrices = computed(
  () => oldPlan.value?.prices.filter((price) => price.details.amount) || []
)

const newPlan = computed(() => billingStore.selectedPlan)
const newPlanItem = computed(() => billingStore.selectedPlanItem)

const formatPriceLocal = (price: BillingPrice | PlanPriceItem) => {
  return formatPrice({
    amount: (price.details?.amount || 0) / 100,
    currency: price.details?.currency,
  })
}
</script>
